import React, { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { BounceLoader } from "react-spinners";
import AnimateLeft from "./Animate/AnimateLeft";
import "../css/About.css";
import UseLoading from "../Reusables/UseLoading";

const About = () => {
  const [loading] = UseLoading();
  const [notification, setNotification] = useState({ show: false, message: "", type: "" });

  // Calculate dynamic experience years (from 2014 to current year)
  const startYear = 2014;
  const currentYear = new Date().getFullYear();
  const experienceYears = currentYear - startYear;

  useEffect(() => {
    if (notification.show) {
      const timer = setTimeout(() => {
        setNotification({ show: false, message: "", type: "" });
      }, 3000);
      return () => clearTimeout(timer);
    }
  }, [notification.show]);

  return (
    <>
      {loading ? (
        <motion.div
          className="about-loader"
          initial={{ opacity: 1 }}
          animate={{ opacity: 0 }}
          transition={{
            duration: 0.5,
            delay: 2.2,
          }}
        >
          <div className="bounce-icon">
            <BounceLoader color="#bfbfbf" />
          </div>
          <div className="loader-text">
            <motion.h5
              initial={{ opacity: 0, x: -50 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{
                duration: 0.5,
                delay: 0.3,
              }}
            >
              About Me
            </motion.h5>
            <motion.h1
              initial={{ opacity: 0, x: -50 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{
                duration: 0.5,
                delay: 0.3,
              }}
            >
              Senior Full Stack Developer | Product-Oriented SaaS Specialist
            </motion.h1>
          </div>
        </motion.div>
      ) : (
        <AnimateLeft>
          <>
            <section className="about-text">
              <div className="wrapper">
                <motion.h5
                  initial={{ opacity: 0, y: 30 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{
                    duration: 0.3,
                    delay: 0.7,
                  }}
                >
                  Gian Esguerra — Santa Rosa, Laguna, Philippines (Remote)
                </motion.h5>
                <motion.h2
                  initial={{ opacity: 0, y: 30 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{
                    duration: 0.3,
                    delay: 1,
                  }}
                >
                  Senior Full Stack Developer with {experienceYears}+ years of experience building and scaling product-led SaaS platforms for complex domains, including e-learning, scientific research, and business intelligence.
                </motion.h2>
                <motion.h3
                  initial={{ opacity: 0, y: 30 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{
                    duration: 0.3,
                    delay: 1.3,
                  }}
                >
                  Expert in end-to-end feature ownership, from collaborating with Product & Design on UX to architecting scalable backends and deploying cloud infrastructure. Proven ability to translate business requirements into intuitive, high-performance applications using React, TypeScript, Node.js, Python, and Rust.
                </motion.h3>
                <motion.h3
                  initial={{ opacity: 0, y: 30 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{
                    duration: 0.3,
                    delay: 1.45,
                  }}
                >
                  +63 930 866 0464 <em> / </em> gianesg19@gmail.com <em> / </em> lucky-leaf.vercel.app
                </motion.h3>
              </div>
            </section>
            <motion.section
              className="about-experience"
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{
                duration: 0.3,
                delay: 1.9,
              }}
            >
              <div className="wrapper">
                <h5>Experience</h5>
                <div className="experience-item">
                  <div className="experience-header">
                    <h3>Senior Full Stack Developer (Remote)</h3>
                    <h4>Chaparral Labs &amp; Be Structured Technology Group (Contract &amp; Full-Time Engagements)</h4>
                    <span className="experience-date">2021 – Present</span>
                  </div>
                  <ul className="experience-details">
                    <li>
                      Led full-stack development of two SaaS platforms serving 1,000+ active users: a cloud-based proteomics research platform for
                      scientists and a multi-tenant business intelligence suite for corporate clients.
                    </li>
                    <li>
                      Owned the complete product development lifecycle for core features, collaborating directly with Product Managers, Designers, and
                      domain experts (researchers, clinicians) to translate complex requirements into intuitive UI workflows, dashboards, and data
                      visualizations.
                    </li>
                    <li>
                      Built modern, responsive frontends using React 18, TypeScript, and Next.js, implementing real-time dashboards, interactive
                      analytics charts (D3.js, Chart.js), and complex data tables (TanStack Table).
                    </li>
                    <li>
                      Architected and scaled high-performance backend systems using Node.js, Python FastAPI, and Rust (Axum), designing multi-tenant
                      REST APIs with JWT authentication, RBAC, and efficient data processing pipelines that improved throughput by 35-45%.
                    </li>
                    <li>
                      Designed and managed cloud infrastructure &amp; DevOps on AWS (S3, Batch, CloudWatch, Secrets Manager), implementing Dockerized
                      CI/CD pipelines with GitHub Actions that reduced deployment cycles from days to hours and ensured 99.9% platform uptime.
                    </li>
                    <li>
                      Integrated critical business systems end-to-end, including Stripe for subscription billing &amp; license management and WorkOS for
                      enterprise SSO, building feature-gating and usage-tracking logic tied to payment status.
                    </li>
                    <li>
                      Executed complex data migrations and modernizations, including transitioning a legacy PHP/Laravel monolith to a modern
                      microservices architecture (Node.js/FastAPI), cutting long-term maintenance effort by 35%.
                    </li>
                    <li>
                      Mentored developers and established engineering best practices, improving code review processes and reducing PR rework by 20%.
                    </li>
                  </ul>
                </div>
                <div className="experience-item">
                  <div className="experience-header">
                    <h3>PHP Developer (Remote)</h3>
                    <h4>Wealth Wealthy Web Innovations — Guam, USA</h4>
                    <span className="experience-date">11/2019 – 05/2021</span>
                  </div>
                  <ul className="experience-details">
                    <li>Delivered 40+ client-facing features across multiple projects using Laravel, WordPress, and Vue.js.</li>
                    <li>Built Vue.js frontends that increased client engagement by ~22%.</li>
                  </ul>
                </div>
              </div>
            </motion.section>
            <motion.section
              className="about-experience"
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{
                duration: 0.3,
                delay: 2.2,
              }}
            >
              <div className="wrapper">
                <h5>Education</h5>
                <div className="experience-item">
                  <div className="experience-header">
                    <h3>Bachelor of Science in Computer Science</h3>
                    <h4>University of Guam</h4>
                    <span className="experience-date">05/2021</span>
                  </div>
                </div>
              </div>
            </motion.section>
            <motion.section
              className="about-experience"
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{
                duration: 0.3,
                delay: 2.5,
              }}
            >
              <div className="wrapper">
                <h5>Technical Skills</h5>
                <div className="experience-item">
                  <div className="experience-header">
                    <h3>Frontend</h3>
                  </div>
                  <ul className="experience-details">
                    <li>React, Next.js, TypeScript, JavaScript (ES6+), Vue.js, Redux Toolkit</li>
                    <li>D3.js, TanStack Table, Tailwind CSS, Chart.js</li>
                    <li>HTML5/CSS3</li>
                  </ul>
                </div>
                <div className="experience-item">
                  <div className="experience-header">
                    <h3>Backend</h3>
                  </div>
                  <ul className="experience-details">
                    <li>Node.js, Python, FastAPI, Rust (Axum), Express.js</li>
                    <li>RESTful API Design, Microservices</li>
                    <li>Authentication (JWT, OAuth, SSO)</li>
                  </ul>
                </div>
                <div className="experience-item">
                  <div className="experience-header">
                    <h3>Databases &amp; ORM</h3>
                  </div>
                  <ul className="experience-details">
                    <li>PostgreSQL, SQLite, MySQL</li>
                    <li>SQLx, Data Modeling, Indexing &amp; Optimization</li>
                    <li>Apache Arrow/DataFusion</li>
                  </ul>
                </div>
                <div className="experience-item">
                  <div className="experience-header">
                    <h3>Cloud &amp; DevOps</h3>
                  </div>
                  <ul className="experience-details">
                    <li>AWS (S3, EC2, Batch, CloudWatch, Secrets Manager, SES)</li>
                    <li>Docker, CI/CD (GitHub Actions), Vercel</li>
                    <li>Linux, Nginx</li>
                  </ul>
                </div>
                <div className="experience-item">
                  <div className="experience-header">
                    <h3>Business Integrations</h3>
                  </div>
                  <ul className="experience-details">
                    <li>Stripe (Payment Processing &amp; Subscriptions)</li>
                    <li>WorkOS (Enterprise SSO)</li>
                    <li>HubSpot API</li>
                  </ul>
                </div>
                <div className="experience-item">
                  <div className="experience-header">
                    <h3>Product &amp; Development Practices</h3>
                  </div>
                  <ul className="experience-details">
                    <li>End-to-End Feature Ownership, SaaS Architecture, Multi-tenancy, RBAC</li>
                    <li>Performance Optimization, Legacy System Migration</li>
                    <li>Agile/Remote Collaboration</li>
                  </ul>
                </div>
              </div>
            </motion.section>
          </>
        </AnimateLeft>
      )}
    </>
  );
};

export default About;